import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatPrice, imgUrl } from "../lib/format";
import { CheckCircle, XCircle, ArrowRight, Spinner, Truck, Calendar } from "@phosphor-icons/react";
import { useCart } from "../lib/cart";
import { useNoIndex } from "../lib/usePageMeta";
import { useConfig } from "../lib/config";
import { firePurchaseConversion } from "../lib/purchase-conversion";
import { trackPaymentFailed } from "../lib/analytics";

// Purchase-conversion emission logic lives in `../lib/purchase-conversion.js`.
// `firePurchaseConversion(order, config)` handles the full 4-channel
// emission (dataLayer purchase + purchase_verified + GA4 gtag + Ads
// conversion) with exactly-once dedup keyed by Stripe session id.

export const PaymentSuccess = () => {
  useNoIndex("/payment/success", {
    title: "Order confirmed",
    description: "Your Medical Peptide Store order was confirmed by Stripe. Tracking will be emailed as soon as a shipping label is generated.",
  });
  const config = useConfig();
  const [params] = useSearchParams();
  const [status, setStatus] = useState({ status: "pending" });
  const [attempts, setAttempts] = useState(0);
  // `phase` drives the UI:
  //   'confirming' — polling Stripe, waiting for paid.
  //   'paid'       — server-verified `paid` — full order details shown.
  //   'unable'     — Stripe hasn't confirmed after the poll budget OR the
  //                  session id is missing/invalid.  We NEVER expose order
  //                  data or fire any conversion in this state.
  const [phase, setPhase] = useState(() => (params.get("session_id") ? "confirming" : "unable"));
  const sessionId = params.get("session_id");
  const { refresh } = useCart();

  const MAX_POLL_ATTEMPTS = 20;

  useEffect(() => {
    if (!sessionId) { setPhase("unable"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        // Trigger server-side finalization via status endpoint (idempotent)
        await api.get(`/payments/status/${sessionId}`).catch(() => {});
        const { data } = await api.get(`/orders/session/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        if (data.status === "paid") {
          setPhase("paid");
          refresh();
          // Server-confirmed → fire conversions.  Guarded by dedup helper.
          firePurchaseConversion(data, config);
          return;
        }
        if (attempts < MAX_POLL_ATTEMPTS) {
          setTimeout(() => setAttempts((a) => a + 1), 2000);
        } else {
          // Exhausted the poll budget without a paid confirmation.
          // Never expose order details, never fire conversions.
          setPhase("unable");
          try { trackPaymentFailed({ reason: "verification_failed" }); } catch (_) {}
        }
      } catch (err) {
        // A 404 from `/orders/session/{sid}` means Stripe has no record
        // of this session id — treat as unable to confirm immediately.
        const notFound = err?.response?.status === 404;
        if (notFound) {
          setPhase("unable");
          try { trackPaymentFailed({ reason: "session_not_found" }); } catch (_) {}
          return;
        }
        if (attempts < MAX_POLL_ATTEMPTS) {
          setTimeout(() => setAttempts((a) => a + 1), 2000);
        } else {
          setPhase("unable");
          try { trackPaymentFailed({ reason: "network_error" }); } catch (_) {}
        }
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, attempts]);

  if (phase === "confirming") {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="payment-success-page" data-clarity-mask="true">
        <Spinner size={64} weight="duotone" className="text-brand-accent mx-auto animate-spin" />
        <h1 className="mt-6 font-display text-3xl font-semibold text-brand-navy">Confirming payment…</h1>
        <p className="mt-3 text-brand-muted text-sm">Please don’t close this page while we verify with Stripe.</p>
      </div>
    );
  }

  if (phase === "unable") {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="payment-success-unable">
        <XCircle size={64} weight="duotone" className="text-amber-500 mx-auto" />
        <h1 className="mt-6 font-display text-3xl font-semibold text-brand-navy" data-testid="payment-success-unable-heading">
          Unable to confirm payment
        </h1>
        <p className="mt-3 text-brand-muted text-sm">
          Stripe hasn’t reported this payment as complete yet.  If you were
          charged, don’t worry — no order details are shown until the
          payment is fully verified.  You can retry in a moment or reach out
          to us and we’ll investigate immediately.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <button
            data-testid="payment-success-retry-btn"
            onClick={() => { setAttempts(0); setPhase("confirming"); }}
            className="inline-flex items-center gap-2 bg-brand-navy text-white hover:bg-brand-navy2 font-semibold px-6 py-3 text-sm"
          >
            Retry verification
          </button>
          <a
            data-testid="payment-success-support-link"
            href="mailto:info@medicalpeptidestore.com?subject=Payment%20confirmation%20issue"
            className="inline-flex items-center gap-2 border border-brand-border text-brand-navy hover:bg-brand-surface font-semibold px-6 py-3 text-sm"
          >
            Contact support
          </a>
        </div>
        <p className="mt-6 text-[10px] text-brand-muted font-mono">
          Reference (share with support if you contact us):{" "}
          <span data-testid="payment-success-unable-session-id">
            {sessionId ? sessionId.slice(0, 24).toUpperCase() : "no-session-id-in-url"}
          </span>
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-16" data-testid="payment-success-page" data-clarity-mask="true">
      <div className="text-center">
        <CheckCircle size={64} weight="duotone" className="text-emerald-400 mx-auto" />
        <h1 className="mt-6 font-display text-3xl md:text-4xl font-semibold text-brand-navy tracking-tight">Payment received</h1>
        <p className="mt-3 text-brand-muted text-sm">Your order has been recorded. A receipt has been sent to your email.</p>
        {status.order_id && (
          <div className="mt-2 font-mono text-xs text-brand-muted tracking-widest" data-testid="order-id">Order #{status.order_id.slice(0, 8).toUpperCase()}</div>
        )}
      </div>

      <div className="mt-6 border border-brand-accent/30 bg-brand-surface p-4 text-[11px] text-brand-navy font-mono uppercase tracking-widest text-center" data-testid="success-research-banner">
        For research use only · Not for human or veterinary use
      </div>

      {/* Items */}
      {status.items?.length > 0 && (
        <div className="mt-10 border border-brand-border divide-y divide-brand-border" data-testid="order-items">
          {status.items.map((it) => (
            <div key={it.product_id} className="p-4 flex items-center gap-4 bg-white">
              {it.thumbnail_image_url && (
                <img src={imgUrl(it.thumbnail_image_url)} alt={it.image_alt_text || it.public_display_name} className="w-14 h-14 object-cover border border-brand-border" loading="lazy" />
              )}
              <div className="flex-1">
                <div className="text-sm text-brand-navy font-display">{it.public_display_name}</div>
                <div className="text-[11px] text-brand-muted font-mono">{it.public_sku} · Qty {it.quantity}</div>
              </div>
              <div className="text-brand-accent font-mono text-sm">{formatPrice((it.unit_amount_cents || 0) * it.quantity)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Totals */}
      <div className="mt-8 border border-brand-border bg-white p-6 grid grid-cols-2 gap-y-2 text-sm" data-testid="order-totals">
        <div className="text-brand-muted">Subtotal</div>
        <div className="text-right font-mono text-brand-navy">{formatPrice(status.subtotal_cents || 0)}</div>
        <div className="text-brand-muted">Shipping{status.shipping_method_name ? ` · ${status.shipping_method_name}` : ""}</div>
        <div className="text-right font-mono text-brand-navy">{formatPrice(status.shipping_cents || 0)}</div>
        <div className="text-brand-muted">Tax</div>
        <div className="text-right font-mono text-brand-navy">{formatPrice(status.tax_cents || 0)}</div>
        <div className="border-t border-brand-border col-span-2 my-2" />
        <div className="font-display text-brand-navy">Total</div>
        <div className="text-right font-mono text-brand-accent font-semibold" data-testid="order-total">{formatPrice(status.total_cents || 0)}</div>
      </div>

      {/* Delivery ETA */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-brand-border bg-white p-5" data-testid="order-ship-date">
          <div className="overline mb-1 flex items-center gap-2"><Calendar size={12} weight="duotone" className="text-brand-accent" /> Expected ship date</div>
          <div className="font-mono text-brand-navy text-sm">{status.estimated_ship_date || "—"}</div>
        </div>
        <div className="border border-brand-border bg-white p-5" data-testid="order-delivery-window">
          <div className="overline mb-1 flex items-center gap-2"><Truck size={12} weight="duotone" className="text-brand-accent" /> Estimated delivery</div>
          <div className="font-mono text-brand-navy text-sm">
            {status.estimated_delivery_start && status.estimated_delivery_end
              ? `${status.estimated_delivery_start} → ${status.estimated_delivery_end}`
              : "—"}
          </div>
        </div>
      </div>

      {/* Tracking (placeholder for now) */}
      <div className="mt-6 border border-brand-border bg-white p-5" data-testid="order-tracking">
        <div className="overline mb-1">Tracking</div>
        {status.tracking_number ? (
          <div className="text-brand-navy text-sm font-mono">
            {status.carrier} · <a href={status.tracking_url} target="_blank" rel="noreferrer" className="text-brand-accent hover:text-brand-accent">{status.tracking_number}</a>
          </div>
        ) : (
          <div className="text-brand-muted text-xs">Tracking will be emailed once your order ships.</div>
        )}
        {status.fulfillment_email_status?.customer === "sent" && (
          <div
            className="mt-2 text-[11px] font-mono text-emerald-700 flex items-center gap-1.5"
            data-testid="order-shipping-confirmation-sent"
          >
            <CheckCircle size={12} weight="duotone" />
            Shipping confirmation emailed to {status.customer_email || "your account address"}.
          </div>
        )}
      </div>

      <div className="mt-6 border border-brand-border bg-white p-5 text-xs text-brand-muted" data-testid="order-claims-info">
        <div className="overline mb-2">Delivery issue?</div>
        Submit a claim within 24 hours of delivery to <a href="mailto:info@medicalpeptidestore.com" className="text-brand-accent hover:text-brand-accent">info@medicalpeptidestore.com</a> with your order number, photos of the box and product, and carrier tracking information. Retain all packaging until reviewed. See the full <Link to="/returns" className="text-brand-accent hover:text-brand-accent underline">Final-Sale &amp; Claims Policy</Link>.
      </div>

      <div className="mt-10 text-center">
        <Link to="/account" data-testid="view-orders-link" className="inline-flex items-center gap-2 bg-brand-navy text-white hover:bg-brand-navy2 font-semibold px-6 py-3 text-sm">
          View orders <ArrowRight size={16} weight="bold" />
        </Link>
      </div>
    </div>
  );
};

export const PaymentCancel = () => {
  useNoIndex("/payment/cancel", {
    title: "Checkout cancelled",
    description: "No charge was made. Your Medical Peptide Store cart is preserved — return whenever you're ready to complete checkout.",
  });
  useEffect(() => {
    try { trackPaymentFailed({ reason: "cancelled" }); } catch (_) {}
  }, []);
  return (
  <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="payment-cancel-page" data-clarity-mask="true">
    <XCircle size={64} weight="duotone" className="text-red-400 mx-auto" />
    <h1 className="mt-6 font-display text-3xl md:text-4xl font-semibold text-brand-navy tracking-tight">Checkout cancelled</h1>
    <p className="mt-3 text-brand-muted text-sm">Your cart items are still saved.</p>
    <Link to="/cart" className="mt-8 inline-flex items-center gap-2 bg-brand-navy text-white hover:bg-brand-navy2 font-semibold px-6 py-3 text-sm">
      Back to cart <ArrowRight size={16} weight="bold" />
    </Link>
  </div>
);
};
